/**
 * Pure display helpers.
 *
 * Their own module rather than exports beside the components that use them, for
 * two reasons that point the same way. `react-refresh/only-export-components`
 * — a warning, and `eslint --max-warnings=0` makes warnings fatal — refuses a
 * module exporting both a component and a function. And every one of these is
 * a decision worth a test of its own: what a mobile number looks like, what a
 * user agent is called, when the evening starts. Testing them through a
 * rendered component would be testing them through three layers of nothing.
 */

/** Initials for an avatar — at most two, from the first two words. */
export function initials(name: string): string {
  const words = name.trim().split(/\s+/).filter(Boolean).slice(0, 2)
  if (words.length === 0) return '?'
  return words.map((word) => (word[0] ?? '').toUpperCase()).join('')
}

/**
 * Whether what was typed into the sign-in field is a phone number.
 *
 * The API takes an email *or* a mobile (`LoginRequest`) and S-D2 draws a
 * toggle, but a toggle is one more thing to get wrong; "optional +, then digits
 * and separators" is unambiguous for the two formats §4 accepts. A wrong guess
 * costs nothing — the answer to both is the same `INVALID_CREDENTIALS`.
 */
export function looksLikeMobile(value: string): boolean {
  return /^\+?[0-9 ()-]{6,}$/.test(value.trim())
}

/** A first guess at an organisation's short name. Always shown before it is used. */
export function slugify(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40)
}

/** Times of day in the tenant's zone, never the browser's or the server's. */
export function greeting(now: Date, timeZone: string): string {
  const hour = Number(
    new Intl.DateTimeFormat('en-GB', { hour: 'numeric', hour12: false, timeZone }).format(now),
  )
  if (hour < 12) return 'Good morning'
  if (hour < 17) return 'Good afternoon'
  return 'Good evening'
}

/** The part of an address before the @, as something to greet a person by. */
export function displayName(email: string | null | undefined, mobile: string | null): string {
  if (email) {
    const local = email.split('@')[0] ?? email
    const first = local.split(/[._-]/)[0] ?? local
    return first.charAt(0).toUpperCase() + first.slice(1)
  }
  return mobile ?? 'there'
}

/** A timestamp as a date a person reads. */
export function formatDate(iso: string): string {
  const parsed = new Date(iso)
  if (Number.isNaN(parsed.getTime())) return iso
  return parsed.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' })
}

/** A timestamp as "how long ago", which is what the sessions panel is asking. */
export function relativeTime(iso: string, now: Date): string {
  const then = new Date(iso)
  if (Number.isNaN(then.getTime())) return iso
  const seconds = Math.round((now.getTime() - then.getTime()) / 1000)
  if (seconds < 90) return 'Just now'
  const minutes = Math.round(seconds / 60)
  if (minutes < 60) return `${String(minutes)} minutes ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${String(hours)} hour${hours === 1 ? '' : 's'} ago`
  const days = Math.round(hours / 24)
  return `${String(days)} day${days === 1 ? '' : 's'} ago`
}

/**
 * A browser string as something a person recognises.
 *
 * Deliberately coarse. A full user agent is unreadable, and the question the
 * sessions panel answers is "is one of these not me?" — for which "Chrome on
 * macOS" is enough and a version number is noise. An agent that matches nothing
 * is shown verbatim rather than guessed at: this is the screen somebody uses to
 * decide whether they have been broken into, and a confident wrong label there
 * is worse than an unfamiliar true one.
 *
 * Order matters. Every Chromium browser carries `Chrome/` and every one of them
 * carries `Safari/`, so the specific tokens are tested first.
 */
export function describeDevice(userAgent: string | null): string {
  if (!userAgent) return 'Unknown device'

  const browser = /Edg\//.test(userAgent)
    ? 'Edge'
    : /OPR\//.test(userAgent)
      ? 'Opera'
      : /Firefox\//.test(userAgent)
        ? 'Firefox'
        : /Chrome\//.test(userAgent)
          ? 'Chrome'
          : /Safari\//.test(userAgent)
            ? 'Safari'
            : /curl\//.test(userAgent)
              ? 'curl'
              : null

  const platform = /iPhone|iPad/.test(userAgent)
    ? 'iOS'
    : /Android/.test(userAgent)
      ? 'Android'
      : /Mac OS X/.test(userAgent)
        ? 'macOS'
        : /Windows/.test(userAgent)
          ? 'Windows'
          : /Linux/.test(userAgent)
            ? 'Linux'
            : null

  if (!browser && !platform) return userAgent.slice(0, 60)
  if (!platform) return browser ?? userAgent.slice(0, 60)
  if (!browser) return platform
  return `${browser} on ${platform}`
}
